import React, { useState, useEffect } from "react";
import {
  useMediaQuery,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  useTheme,
  Typography,
} from "@mui/material";
import FileUploader from "./FileUploader";
import NameSection from "./NameSection";
import Results from "./Results";
import { useExcelParser } from "./useExcelParser";
import ConfrimationStep from "./ConfirmationStep";
import useAuth from "../../../../hooks/useAuth";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../../redux/slices/snackbar";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../../config";

function Modal({ open, setOpen, getFleet }) {
  const isUnder600px = useMediaQuery("(max-width:600px)");
  const isUnder1000px = useMediaQuery("(max-width:1000px)");
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [tableData, setTableData] = useState([]);
  const [loadingFleet, setLoadingFleet] = useState(false);
  const [uploadConfirmation, setUploadConfirmation] = useState(false);
  const [fleetMaxLengthExtended, setFleetMaxLengthExtended] = useState(false);

  const theme = useTheme();
  const dispatch = useDispatch();
  const { parsedData, error: parserError } = useExcelParser(file);
  const navigate = useNavigate();

  let maxWidth;
  if (isUnder600px) {
    maxWidth = "xs";
  } else if (isUnder1000px) {
    maxWidth = "sm";
  } else {
    maxWidth = uploadConfirmation ? "sm" : "lg";
  }

  // Strict regex for Targa
  const plateRegex =
    /^(?:[A-Z]{2}[0-9]{3}[A-Z]{2}|[A-Z]{2}[0-9]{5,6}|[A-Z]{2}·[A-Z][0-9]{5}|[A-Z]{2}[0-9]{4}[A-Z]{2}|[A-Z]{2}[0-9]{5}|[0-9]{6}|[A-Z][0-9]{6}|[0-9]{5}[A-Z]|[A-Z][0-9]{4}[A-Z]|[A-Z]{2}[0-9]{3}[A-Z]|X[A-Z][0-9]{3}[A-Z]{2}|C[CD][0-9]{3}[A-Z]{2}|CC[0-9]{3}[A-Z]|E[I][0-9]{3}[A-Z]{2}|[A-Z][0-9]{3}POLIZIA|EE[0-9]{3}[A-Z]{2}|[A-Z][0-9]{3}TAXI|T[0-9]{4}[A-Z]|ASI[0-9]{4}|[A-Z]{1,3}[0-9]{1,4}[A-Z]{0,3}|[A-Za-z0-9]{6})$/;

  // Function to validate if a string is a valid date (dd/mm/yyyy) and exists
  const isValidDate = (dateString) => {
    const regEx = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!dateString.match(regEx)) return false; // Invalid format

    const [day, month, year] = dateString.split("/").map(Number);
    const date = new Date(year, month - 1, day);

    // Check if JS date object rolled over (e.g., Feb 30 becomes March 2)
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  const validConfigCodes = ["00001", "00002", "00003"];

  const handleSnackbar = (message, severity) => {
    dispatch(showSnackbar({ message: message, severity: severity }));
  };

  // Handle file upload
  const handleFileAccepted = (files) => {
    setFile(files[0]);
  };

  useEffect(() => {
    if (parsedData) {
      const formattedData = parsedData.map((row) => {
        const errors = [];

        // Validate TARGA
        const targaVal = row["TARGA"]?.toString().trim() || "";
        if (!plateRegex.test(targaVal)) {
          errors.push("TARGA errata");
        }

        // Validate CODICE CONFIGURAZIONE
        const codiceVal = row["CODICE CONFIGURAZIONE"]?.toString().trim() || "";
        if (!validConfigCodes.includes(codiceVal)) {
          errors.push("CODICE CONFIGURAZIONE errato");
        }

        // Validate Dates
        [
          "DATA INIZIO COPERTURA",
          "DATA FINE COPERTURA",
          "DATA RENDICONTAZIONE",
        ].forEach((dateKey) => {
          const dateVal = row[dateKey]?.toString().trim() || "";
          if (!isValidDate(dateVal)) {
            errors.push(`${dateKey} errata`);
          }
        });

        // Validate COLLABORATORE
        const collaboratoreVal = row["COLLABORATORE"];
        if (
          !collaboratoreVal ||
          isNaN(collaboratoreVal) ||
          Number(collaboratoreVal) <= 0
        ) {
          errors.push("COLLABORATORE errato");
        }

        return {
          row: row.id,
          targa: targaVal,
          codice: row["CODICE CONFIGURAZIONE"],
          inizio: row["DATA INIZIO COPERTURA"],
          fine: row["DATA FINE COPERTURA"],
          rendicontazione: row["DATA RENDICONTAZIONE"],
          collaboratore: collaboratoreVal,
          error: errors.length > 0,
          message: errors.length > 0 ? errors.join(", ") : "Dati corretti",
        };
      });

      setTableData(formattedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedData]);

  const toggleConfrimationStep = () => {
    setFleetMaxLengthExtended(false);
    setUploadConfirmation((prev) => !prev);
  };

  const resetState = () => {
    setFile(null);
    setName("");
    setTableData([]);
    setUploadConfirmation(false);
    setFleetMaxLengthExtended(false);
    setOpen(false);
  };

  const handleClose = (_, reason) => {
    if (reason && reason === "backdropClick") return;
    resetState();
  };

  const handleChangeName = (e) => {
    setName(e.target.value);
  };

  const handleUploadData = async () => {
    const validRows = tableData.filter((row) => !row.error);
    const validatedPlates = validRows.map((row) => row.targa);

    // Create JSON of valid rows with all fields
    const validRowsJson = validRows.map((row) => ({
      targa: row.targa,
      codiceConfigurazione: row.codice,
      dataInizioCopertura: row.inizio,
      dataFineCopertura: row.fine,
      dataRendicontazione: row.rendicontazione,
      collaboratore: row.collaboratore,
    }));

    const token = sessionStorage.getItem("token");
    if (!token) return;
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          nome: name,
          numeroOggetti: validatedPlates.length,
          data: validRowsJson,
        }),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/assStAxaSave`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error("Failed to upload data");
      }

      const data = await response.json();
      if (data.status === -1) {
        navigate("/auth/sign-in");
        handleSnackbar(data.message || "Qualcosa è andato storto", "error");
      }
      if (data.status === 0) {
        navigate("/private");
        handleSnackbar(data.message || "Qualcosa è andato storto", "error");
      }
      if (data.status === 1) {
        getFleet();
        handleSnackbar(
          data.message || "Flotta caricata correttamente",
          "success"
        );
        handleClose();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      fullWidth={true}
      maxWidth={maxWidth}
    >
      <DialogTitle id="form-dialog-title">Crea Flotta</DialogTitle>
      <DialogContent>
        {parserError && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {parserError}
          </Typography>
        )}
        {!file ? (
          <FileUploader onFileAccepted={handleFileAccepted} />
        ) : uploadConfirmation ? (
          <ConfrimationStep
            theme={theme}
            tableData={tableData}
            fleetMaxLengthExtended={fleetMaxLengthExtended}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginBottom: "8px",
              marginTop: "8px",
            }}
          >
            <NameSection
              theme={theme}
              name={name}
              handleChangeName={handleChangeName}
              fileName={file.name}
            />
            <Divider sx={{ margin: "16px 0" }} />
            <Results data={tableData} theme={theme} file={file} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={uploadConfirmation ? toggleConfrimationStep : handleClose}
          color="primary"
          sx={{ marginRight: "8px" }}
        >
          {uploadConfirmation ? "Indietro" : "Chiudi"}
        </Button>

        {file && !parserError && (
          <Button
            disabled={
              !tableData?.some((item) => !item.error) || !name || loadingFleet
            }
            type="submit"
            color="primary"
            variant="contained"
            onClick={
              !uploadConfirmation ? toggleConfrimationStep : handleUploadData
            }
            sx={{ marginRight: "16px" }}
          >
            {loadingFleet ? (
              <CircularProgress size={22} />
            ) : uploadConfirmation ? (
              "Conferma caricamento"
            ) : (
              "Crea"
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default Modal;
